import { useState, useEffect, useCallback, useRef } from 'react';
import { useNostrQuery } from './useNostrQuery';
import { useNostrPublish, getReadRelays } from './useNostrPublish';

// ══════════════════════════════════════════════════════════════════
//  FOLLOW LIST — priority-relay architecture
//
//  The follow list (kind 3) is the MOST DANGEROUS event to publish:
//  a single publish with an incomplete list WIPES the entire contact
//  list on all relays. We protect against this with:
//
//  1. Priority relay — relay.bchnostr.com is queried FIRST for the
//     current user's kind 3. It's authoritative, fast, never truncates.
//  2. Global singleton — all components share one authoritative list
//  3. localStorage backup — survives page reloads
//  4. "Ready" gate — follow/unfollow is blocked until relay data loads
//  5. Catastrophic-loss guard — refuse to publish if the new list is
//     dramatically smaller than the known list (>50% loss)
//  6. Pick newest event — if multiple kind 3 events arrive, use the
//     one with the highest created_at
//
//  Cache strategy: Follow lists are cached for 2 HOURS. They only
//  change when the user explicitly follows/unfollows (which updates
//  local state immediately). There's no reason to re-query relays
//  every 5 minutes — that was causing instability as partial relay
//  responses produced intermediate states.
// ══════════════════════════════════════════════════════════════════

// ── Priority relays for kind 3 ───────────────────────────────────
// Queried first and separately from the general relay pool.
// relay.bchnostr.com = our own relay (authoritative for BCHNostr users)
// purplepag.es = specialized contact-list relay (good fallback)
const PRIORITY_RELAYS = [
  'wss://relay.bchnostr.com',
  'wss://purplepag.es',
];

// Fallback relays (only used if priority relays have no data)
const FALLBACK_RELAYS = [
  'wss://nos.lol',
  'wss://relay.primal.net',
];

const FOLLOWS_STORE_KEY = 'bchnostr:follows';
const FOLLOWS_TS_KEY = 'bchnostr:follows-ts';

// 2-hour cache — kind 3 only changes on explicit follow/unfollow
const FOLLOWS_CACHE_TTL = 2 * 60 * 60 * 1000;

// ── Global state — shared by all hook instances ──────────────────

let _globalFollows: string[] = [];
let _globalReady = false;
let _globalPubkey: string | null = null;
let _globalTimestamp = 0; // created_at of the kind 3 event we're using
let _globalFromBackup = false; // true when state was loaded from localStorage, not relays
let _globalFetchedAt = 0; // when we last fetched from relays
let _globalFetchInProgress = false;
const _listeners = new Set<() => void>();

function notifyListeners() {
  _listeners.forEach(fn => fn());
}

/** Load follows from localStorage backup */
function loadBackup(pubkey: string): { follows: string[]; ts: number; fetchedAt: number } {
  try {
    const raw = localStorage.getItem(FOLLOWS_STORE_KEY + ':' + pubkey);
    const ts = parseInt(localStorage.getItem(FOLLOWS_TS_KEY + ':' + pubkey) || '0', 10);
    const fetchedAt = parseInt(localStorage.getItem(FOLLOWS_STORE_KEY + ':' + pubkey + ':fetched') || '0', 10);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return { follows: parsed, ts, fetchedAt };
      }
    }
  } catch {}
  return { follows: [], ts: 0, fetchedAt: 0 };
}

/** Save follows to localStorage backup */
function saveBackup(pubkey: string, follows: string[], ts: number) {
  try {
    localStorage.setItem(FOLLOWS_STORE_KEY + ':' + pubkey, JSON.stringify(follows));
    localStorage.setItem(FOLLOWS_TS_KEY + ':' + pubkey, String(ts));
    localStorage.setItem(FOLLOWS_STORE_KEY + ':' + pubkey + ':fetched', String(Date.now()));
  } catch {}
}

// ── Priority relay fetch ─────────────────────────────────────────
// Fetches kind 3 directly from priority relays via WebSocket.
// Returns the newest kind 3 event found, or null if none found.
// This bypasses useNostrQuery entirely — no caching quirks, no
// batching, no stale filters.

interface Kind3Result {
  follows: string[];
  timestamp: number;
  source: string; // which relay it came from
}

function fetchKind3FromRelays(
  pubkey: string,
  relayUrls: string[],
  timeoutMs: number,
): Promise<Kind3Result | null> {
  return new Promise((resolve) => {
    let best: Kind3Result | null = null;
    let resolved = false;
    const sockets: WebSocket[] = [];
    let responsesReceived = 0;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      sockets.forEach(ws => { try { ws.close(); } catch {} });
      resolve(best);
    };

    const deadline = setTimeout(finish, timeoutMs);

    for (const url of relayUrls) {
      try {
        const ws = new WebSocket(url);
        sockets.push(ws);
        const subId = 'k3_' + Math.random().toString(36).slice(2, 8);

        ws.onopen = () => {
          ws.send(JSON.stringify(['REQ', subId, {
            kinds: [3],
            authors: [pubkey],
            limit: 3, // relays may have slightly different versions
          }]));
        };

        ws.onmessage = (msg) => {
          try {
            const data = JSON.parse(msg.data);
            if (data[0] === 'EVENT' && data[1] === subId) {
              const ev = data[2];
              const follows: string[] = (ev.tags as string[][])
                .filter((t: string[]) => t[0] === 'p' && t[1]?.length === 64)
                .map((t: string[]) => t[1]);
              const unique: string[] = [...new Set(follows)];

              // Keep the newest event. On timestamp tie, keep the one with more follows.
              if (!best ||
                  ev.created_at > best.timestamp ||
                  (ev.created_at === best.timestamp && unique.length > best.follows.length)) {
                best = { follows: unique, timestamp: ev.created_at, source: url };
              }
            }
            if (data[0] === 'EOSE' && data[1] === subId) {
              responsesReceived++;
              // If the first priority relay responded with data, resolve immediately.
              // No need to wait for slower relays — the priority relay is authoritative.
              if (best && url === relayUrls[0]) {
                clearTimeout(deadline);
                finish();
              }
              // If all relays have responded, we're done
              if (responsesReceived >= relayUrls.length) {
                clearTimeout(deadline);
                finish();
              }
            }
          } catch {}
        };

        ws.onerror = () => {
          responsesReceived++;
          if (responsesReceived >= relayUrls.length) { clearTimeout(deadline); finish(); }
        };
      } catch {
        responsesReceived++;
        if (responsesReceived >= relayUrls.length) { clearTimeout(deadline); finish(); }
      }
    }
  });
}

/**
 * Fetch the current user's follow list from priority relays, falling back
 * to general relays. Updates the global singleton.
 */
async function fetchCurrentUserFollows(pubkey: string): Promise<void> {
  if (_globalFetchInProgress) return;
  _globalFetchInProgress = true;

  try {
    // Step 1: Try priority relays first (fast, authoritative)
    let result = await fetchKind3FromRelays(pubkey, PRIORITY_RELAYS, 5000);

    // Step 2: If priority relays had nothing, try fallback relays
    if (!result) {
      result = await fetchKind3FromRelays(pubkey, FALLBACK_RELAYS, 8000);
    }

    // Step 3: STILL nothing — widen to the user's full configured read-relay
    // set. This is the critical fix for "lists empty on a fresh browser":
    // a user whose kind-3 contact list lives on relays outside our small
    // hardcoded priority/fallback set would otherwise get an empty follow
    // list, which in turn empties notifications' friend feed and the profile
    // follow count. Querying their real read relays finds the list wherever
    // it actually lives. Deduped against relays we've already tried.
    if (!result) {
      const tried = new Set([...PRIORITY_RELAYS, ...FALLBACK_RELAYS]);
      const wider = getReadRelays().filter(r => !tried.has(r));
      if (wider.length > 0) {
        result = await fetchKind3FromRelays(pubkey, wider, 8000);
      }
    }

    if (result && result.follows.length > 0) {
      // Only accept relay data if it's genuinely newer or better than what we have.
      //
      // IMPORTANT: When _globalFromBackup is true, the backup timestamp is from
      // the user's last successful publish. Relay data MUST be at least as new
      // as that timestamp to be accepted. Previously, _globalFromBackup caused
      // unconditional acceptance, which meant stale relay data (from before
      // the last follow/unfollow) could overwrite the correct local backup.
      const isNewer = result.timestamp > _globalTimestamp;
      const isSameTimestampWithMoreFollows =
        result.timestamp === _globalTimestamp && result.follows.length > _globalFollows.length;

      if (isNewer || isSameTimestampWithMoreFollows) {
        _globalFollows = result.follows;
        _globalTimestamp = result.timestamp;
        _globalFromBackup = false;
        _globalReady = true;
        _globalFetchedAt = Date.now();
        saveBackup(pubkey, result.follows, result.timestamp);
        notifyListeners();
      } else {
        // Relay data is not newer — keep our local state but mark as relay-confirmed
        _globalFromBackup = false;
        _globalReady = true;
        _globalFetchedAt = Date.now();
        notifyListeners();
      }
    } else if (_globalFollows.length > 0) {
      // Relays returned nothing but we have backup data — mark as ready.
      // Keep the backup data as authoritative.
      _globalFromBackup = false;
      _globalReady = true;
      _globalFetchedAt = Date.now();
      notifyListeners();
    }
  } finally {
    _globalFetchInProgress = false;
  }
}

// ── Public API ───────────────────────────────────────────────────

/**
 * Get the follow list for a pubkey.
 *
 * CURRENT USER: Uses the priority-relay architecture with global singleton,
 * localStorage backup, and 2-hour cache. Rock-solid and stable.
 *
 * OTHER USERS: Uses an independent useNostrQuery with local state.
 * Slightly less stable but doesn't affect the current user's state.
 */
export function useFollows(pubkey: string | undefined) {
  const [, forceUpdate] = useState(0);

  // ── Determine if this is the current user's follow list ──
  const isCurrentUser = !pubkey ? false
    : _globalPubkey === null || _globalPubkey === pubkey;

  // ── Initialize global singleton (current user only) ──
  useEffect(() => {
    if (!pubkey || !isCurrentUser) return;
    if (_globalPubkey !== pubkey) {
      _globalPubkey = pubkey;
      const backup = loadBackup(pubkey);
      _globalFollows = backup.follows;
      _globalTimestamp = backup.ts;
      _globalFetchedAt = backup.fetchedAt;
      _globalFromBackup = backup.follows.length > 0;
      _globalReady = backup.follows.length > 0;
      notifyListeners();
    }

    // Fetch from relays if cache is stale (older than 2 hours)
    const cacheAge = Date.now() - _globalFetchedAt;
    if (cacheAge > FOLLOWS_CACHE_TTL || _globalFromBackup || _globalFollows.length === 0) {
      fetchCurrentUserFollows(pubkey);

      // Safety net: if relays are slow/unreachable and we have backup data,
      // trust the backup after 3 seconds so the user can still follow/unfollow.
      // Mobile connections are often slow — 10s was too long, users gave up.
      // First timer at 3s (most mobile users), second at 6s (edge cases).
      if (_globalFromBackup && _globalFollows.length > 0) {
        const timer = setTimeout(() => {
          if (_globalFromBackup) {
            _globalFromBackup = false;
            _globalReady = true;
            _globalFetchedAt = Date.now();
            notifyListeners();
          }
        }, 3000);
        return () => clearTimeout(timer);
      }

      // Even if no backup data, set ready after 5s so the button appears.
      // New users with empty follow lists were stuck permanently loading.
      if (!_globalReady && _globalFollows.length === 0) {
        const timer = setTimeout(() => {
          if (!_globalReady) {
            _globalReady = true;
            _globalFromBackup = false;
            _globalFetchedAt = Date.now();
            notifyListeners();
          }
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [pubkey, isCurrentUser]);

  // Subscribe to global changes
  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    _listeners.add(listener);
    return () => { _listeners.delete(listener); };
  }, []);

  // ── Other users: independent relay query ──────────────────────
  // Uses useNostrQuery which has replaceable-event dedup built in.
  const { events, loading } = useNostrQuery({
    filters: pubkey && !isCurrentUser ? [{ kinds: [3], authors: [pubkey], limit: 5 }] : [],
    enabled: !!pubkey && !isCurrentUser,
    allRelays: true,
  });

  const [otherFollows, setOtherFollows] = useState<string[]>([]);
  const [otherLoading, setOtherLoading] = useState(true);
  const lastBestIdRef = useRef<string>('');

  useEffect(() => {
    if (isCurrentUser || !pubkey || events.length === 0) return;

    // Pick the newest event (on tie, most p-tags)
    let best = events[0];
    let bestPTagCount = best.tags.filter(t => t[0] === 'p').length;
    for (let i = 1; i < events.length; i++) {
      const ev = events[i];
      const pCount = ev.tags.filter(t => t[0] === 'p').length;
      if (ev.created_at > best.created_at) {
        best = ev; bestPTagCount = pCount;
      } else if (ev.created_at === best.created_at && pCount > bestPTagCount) {
        best = ev; bestPTagCount = pCount;
      }
    }

    if (best.id === lastBestIdRef.current) return;
    lastBestIdRef.current = best.id;

    const follows = best.tags
      .filter(t => t[0] === 'p' && t[1]?.length === 64)
      .map(t => t[1]);
    setOtherFollows([...new Set(follows)]);
    setOtherLoading(false);
  }, [events, pubkey, isCurrentUser]);

  useEffect(() => {
    if (!isCurrentUser && !loading && events.length === 0) {
      setOtherLoading(false);
    }
  }, [loading, events.length, isCurrentUser]);

  if (isCurrentUser) {
    return {
      follows: _globalFollows,
      loading: !_globalReady,
      ready: _globalReady,
    };
  }

  return {
    follows: otherFollows,
    loading: otherLoading,
    ready: false,
  };
}

/**
 * Safe follow/unfollow mutation.
 *
 * Publishes the updated kind 3 to:
 * 1. relay.bchnostr.com (priority — authoritative)
 * 2. purplepag.es (contact-list relay)
 * 3. All the user's configured write relays
 *
 * Safety checks:
 * - Blocks if the follow list hasn't loaded yet
 * - Refuses to publish if the result would be empty
 * - Catastrophic-loss guard prevents >50% shrinkage
 */
export function useFollowMutation() {
  const { publish } = useNostrPublish();
  const publishingRef = useRef(false);

  const mutate = useCallback(async (
    action: 'follow' | 'unfollow',
    targetPubkey: string,
  ): Promise<boolean> => {
    if (publishingRef.current) return false;

    if (!_globalPubkey) {
      console.warn('[useFollows] Cannot mutate: no pubkey');
      return false;
    }
    // Allow mutation if we have backup data OR relay-confirmed data.
    // Previously this blocked on _globalFromBackup, which meant mobile
    // users with slow relay connections couldn't follow anyone for 10+ seconds.
    // The backup IS the user's last published follow list — it's safe to mutate from.
    if (!_globalReady && _globalFollows.length === 0) {
      console.warn('[useFollows] Cannot mutate: follow list not loaded yet');
      return false;
    }

    const currentFollows = _globalFollows;
    let newFollows: string[];

    if (action === 'follow') {
      if (currentFollows.includes(targetPubkey)) return true;
      newFollows = [...currentFollows, targetPubkey];
    } else {
      newFollows = currentFollows.filter(f => f !== targetPubkey);
    }

    if (newFollows.length === 0 && currentFollows.length > 0) {
      console.warn('[useFollows] Refusing to publish empty contact list');
      return false;
    }

    if (currentFollows.length > 10 && newFollows.length < currentFollows.length * 0.5) {
      console.warn('[useFollows] Refusing to publish: would lose >50% of follows');
      return false;
    }

    publishingRef.current = true;
    try {
      const now = Math.floor(Date.now() / 1000);

      // publish() sends to the user's configured write relays.
      // We also explicitly publish to priority relays below.
      await publish({
        kind: 3,
        content: '',
        tags: newFollows.map(f => ['p', f]),
        created_at: now,
      });

      // Update global state immediately
      _globalFollows = newFollows;
      _globalTimestamp = now;
      _globalFromBackup = false;
      _globalFetchedAt = Date.now(); // reset cache timer
      saveBackup(_globalPubkey!, newFollows, now);
      notifyListeners();

      return true;
    } catch (err) {
      console.error('[useFollows] Failed to publish kind 3:', err);
      return false;
    } finally {
      publishingRef.current = false;
    }
  }, [publish]);

  // Ready if: relay-confirmed, OR we have backup data (safe to mutate from),
  // OR the list is empty but we've finished loading (new user).
  return { mutate, ready: _globalReady || (_globalFromBackup && _globalFollows.length > 0) };
}

export function useIsFollowing(myPubkey: string | undefined, targetPubkey: string) {
  const { follows } = useFollows(myPubkey);
  return follows.includes(targetPubkey);
}

/**
 * Get the list of pubkeys that follow a given user.
 * Queries kind 3 events that tag the target pubkey with #p.
 * Uses allRelays for better coverage since not all relays index #p on kind 3.
 */
export function useFollowers(pubkey: string | undefined) {
  const [followers, setFollowers] = useState<string[]>([]);
  const prevEventCountRef = useRef(0);

  const { events, loading } = useNostrQuery({
    filters: pubkey ? [{ kinds: [3], '#p': [pubkey], limit: 1000 }] : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  useEffect(() => {
    if (!pubkey || events.length === 0) return;

    if (events.length === prevEventCountRef.current) return;
    prevEventCountRef.current = events.length;

    const confirmedFollowers: string[] = [];
    for (const event of events) {
      const stillFollows = event.tags.some(t => t[0] === 'p' && t[1] === pubkey);
      if (stillFollows && event.pubkey && typeof event.pubkey === 'string') {
        confirmedFollowers.push(event.pubkey);
      }
    }

    setFollowers(prev => {
      if (prev.length === confirmedFollowers.length &&
          prev.every((pk, i) => pk === confirmedFollowers[i])) {
        return prev;
      }
      return confirmedFollowers;
    });
  }, [events, pubkey]);

  return { followers, followerCount: followers.length, loading };
}

